import { useEffect, useRef } from "react";
import { ArrowRight, ShieldCheck, MapPin, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import BackButton from "../../pages/components/BackButton";
import { fmtEur } from "../../lib/api";
import { t, pickName } from "../../lib/i18n";
import { useBooking, STEP_SLOT } from "./BookingContext";

function localeFor(locale) {
  if (locale === "en") return "en-GB";
  if (locale === "pt") return "pt-PT";
  if (locale === "ca") return "ca-ES";
  return "es-ES";
}

/**
 * Formulario final con:
 *  - Tipo de cliente (Particular ↔ Empresa con datos fiscales).
 *  - Vehículo + matrícula + datos contacto.
 *  - Recogida a domicilio (Sprint F) si el centro la ofrece.
 *  - Turnstile widget si el config lo activa.
 *  - CTA confirmar respetando brand_color del centro.
 */
export default function CustomerDataForm() {
  const {
    data, service, slot, total, locale, form, setForm, cfg,
    turnstileToken, setTurnstileToken, busy, submit, setStep,
  } = useBooking();
  const tr = (k, params) => t(k, locale, params);
  const org = data?.org || {};
  const brandColor = (org.brand_color || "#0033A0").trim();
  const turnstileRef = useRef(null);

  // Cargar widget Cloudflare Turnstile cuando esté habilitado
  useEffect(() => {
    if (!cfg?.turnstile_enabled || !cfg?.turnstile_site_key) return;
    if (document.getElementById("cf-turnstile-script")) {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: cfg.turnstile_site_key,
          callback: (tk) => setTurnstileToken(tk),
        });
      }
      return;
    }
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
    s.async = true; s.defer = true;
    window.onTurnstileLoad = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: cfg.turnstile_site_key,
          callback: (tk) => setTurnstileToken(tk),
        });
      }
    };
    document.body.appendChild(s);
  }, [cfg, setTurnstileToken]);

  if (!service || !slot) return null;
  const turnstileBlocked = cfg.turnstile_enabled && cfg.turnstile_site_key && !turnstileToken;
  const empresaIncomplete = form.customer_type === "empresa" && (!form.razon_social || !form.nif_cif || !form.address_fiscal);
  const submitDisabled = busy || !form.license_plate || !form.customer_name || !(form.customer_email || form.customer_phone) || empresaIncomplete || turnstileBlocked;

  return (
    <>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
        {pickName(service, locale)} · {new Date(slot.iso).toLocaleDateString(localeFor(locale))} · {slot.time} · <b>{fmtEur(total)}</b>
      </p>
      <h2 className="font-display text-4xl font-bold tracking-tighter mt-2 text-[#1A1A1A]">{tr("datos")}</h2>
      <div className="bg-white border border-gray-200 rounded-sm p-6 mt-6 space-y-4 max-w-xl">
        <div>
          <Label className="text-xs uppercase tracking-wider">Tipo de cliente *</Label>
          <div className="mt-1.5 grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded border border-gray-200">
            <button type="button" onClick={() => setForm({ ...form, customer_type: "particular" })}
              className={`px-3 py-2 text-xs uppercase tracking-wider rounded font-semibold transition-colors ${form.customer_type === "particular" ? "bg-[#0077B6] text-white" : "text-gray-500 hover:text-gray-900"}`}
              data-testid="pub-type-particular">
              Particular · Ticket
            </button>
            <button type="button" onClick={() => setForm({ ...form, customer_type: "empresa" })}
              className={`px-3 py-2 text-xs uppercase tracking-wider rounded font-semibold transition-colors ${form.customer_type === "empresa" ? "bg-[#FFB703] text-[#1A1A1A]" : "text-gray-500 hover:text-gray-900"}`}
              data-testid="pub-type-empresa">
              Empresa · Factura
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">{tr("matricula")} *</Label>
            <Input value={form.license_plate}
              onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
              required className="mt-1 rounded-sm font-mono-tab" data-testid="pub-plate" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">{tr("marca.modelo")}</Label>
            <Input value={form.car_make_model}
              onChange={(e) => setForm({ ...form, car_make_model: e.target.value })}
              className="mt-1 rounded-sm" placeholder="Ej. SEAT León" data-testid="pub-car" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">
            {form.customer_type === "empresa" ? "Nombre del contacto *" : `${tr("tu.nombre")} *`}
          </Label>
          <Input value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            required className="mt-1 rounded-sm" data-testid="pub-name" />
        </div>

        {form.customer_type === "empresa" && (
          <div className="bg-[#FFB703]/10 border border-[#FFB703]/30 rounded-sm p-4 space-y-3" data-testid="pub-empresa-block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold">Datos fiscales (factura completa)</div>
            <div><Label className="text-xs uppercase tracking-wider">Razón social *</Label>
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} required className="mt-1 rounded-sm" placeholder="Logistic Sevilla SL" data-testid="pub-razon" /></div>
            <div><Label className="text-xs uppercase tracking-wider">CIF *</Label>
              <Input value={form.nif_cif} onChange={(e) => setForm({ ...form, nif_cif: e.target.value.toUpperCase() })} required className="mt-1 rounded-sm font-mono-tab uppercase" placeholder="B12345678" data-testid="pub-cif" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Dirección fiscal *</Label>
              <Input value={form.address_fiscal} onChange={(e) => setForm({ ...form, address_fiscal: e.target.value })} required className="mt-1 rounded-sm" placeholder="Av. Comercio 45, 41005 Sevilla" data-testid="pub-fiscal" /></div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">{tr("email")} <span className="text-[var(--ag-text-muted)] normal-case font-normal">{tr("opcional")}</span></Label>
            <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1 rounded-sm" data-testid="pub-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
              <WhatsappLogo size={12} weight="fill" className="text-[#25D366]" /> {tr("telefono")} *
            </Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} required className="mt-1 rounded-sm" placeholder="+34..." data-testid="pub-phone" />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 -mt-2">{tr("indica.uno")}</p>
        <div>
          <Label className="text-xs uppercase tracking-wider">{tr("notas")}</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" placeholder={tr("notas.placeholder")} />
        </div>

        {org.offer_pickup_service && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2" data-testid="pub-pickup-card">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.pickup_service}
                onChange={(e) => setForm({ ...form, pickup_service: e.target.checked })}
                className="accent-amber-600" data-testid="pub-pickup-toggle" />
              <MapPin size={14} weight="fill" className="text-amber-600" />
              <span className="font-semibold text-amber-900">Recogida a domicilio</span>
              {!!org.pickup_extra_fee && <span className="ml-auto text-xs font-semibold text-amber-700">+ {fmtEur(org.pickup_extra_fee)}</span>}
            </label>
            {form.pickup_service && (
              <div className="space-y-2 pt-1">
                <Input value={form.pickup_address}
                  onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                  placeholder="Calle, número, ciudad" className="rounded-sm" required data-testid="pub-pickup-address" />
                <Input value={form.pickup_time_preference}
                  onChange={(e) => setForm({ ...form, pickup_time_preference: e.target.value })}
                  placeholder="Horario preferido (ej. mañana 9-11)" className="rounded-sm" data-testid="pub-pickup-time" />
                <p className="text-[10px] text-amber-700">Radio máx: {org.pickup_radius_km || 10} km · El taller te confirmará por chat.</p>
              </div>
            )}
          </div>
        )}

        {cfg.turnstile_enabled && cfg.turnstile_site_key ? (
          <div className="pt-2"><div ref={turnstileRef} data-testid="turnstile-widget" /></div>
        ) : (
          <div className="pt-2 flex items-center gap-2 text-[11px] text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-sm px-3 py-2" data-testid="turnstile-placeholder">
            <ShieldCheck size={14} weight="duotone" /> {tr("captcha.mock")}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <BackButton onClick={() => setStep(STEP_SLOT)} label={tr("cambiar.hora")} variant="light" testId="pub-back-data" />
          <Button onClick={submit} disabled={submitDisabled}
            className="rounded-sm flex-1 text-[var(--ag-text)] hover:opacity-90 transition-opacity"
            style={{ backgroundColor: brandColor }}
            data-testid="pub-confirm">
            {busy ? tr("reservando") : (
              <span className="flex items-center justify-center gap-2">
                {tr("confirmar.reserva")} · {fmtEur(total)} <ArrowRight weight="bold" />
              </span>
            )}
          </Button>
        </div>
        <p className="text-[10px] text-gray-500 leading-relaxed">{tr("rgpd", { org: org.name, nif: org.nif })}</p>
      </div>
    </>
  );
}
